import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  makeStyles,
  IconButton,
  Collapse,
  Box,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import BusinessIcon from "@material-ui/icons/Business";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  dialogPaper: {
    backgroundColor: theme.palette.type === "dark" ? "#1E2126" : "#FFFFFF",
    borderRadius: 16,
    border: `1px solid ${theme.palette.primary.main}40`,
    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.45)",
    color: theme.palette.type === "dark" ? "#E2E8F0" : theme.palette.text.primary,
    overflow: "hidden",
  },
  dialogHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    "& h2": {
      display: "flex",
      alignItems: "center",
      fontSize: "1.15rem",
      fontWeight: 600,
      margin: 0,
      color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
    },
  },
  helpBox: {
    padding: theme.spacing(1.5, 3),
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.08)" : "rgba(0, 180, 216, 0.05)",
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.2)" : "rgba(0, 180, 216, 0.15)"
    }`,
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  dialogActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.02)",
    gap: 8,
  },
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
}));

const BrandModal = ({ open, onClose, brandId, onSave }) => {
  const classes = useStyles();
  const [name, setName] = useState("");
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const fetchBrand = async () => {
      if (!brandId) return;
      try {
        const { data } = await api.get(`/brands/${brandId}`);
        setName(data.name);
      } catch (err) {
        toastError(err);
      }
    };
    fetchBrand();
  }, [brandId, open]);

  const handleClose = () => {
    setName("");
    setShowHelp(false);
    onClose();
  };

  const handleSave = async () => {
    try {
      let data;
      if (brandId) {
        const res = await api.put(`/brands/${brandId}`, { name });
        data = res.data;
        toast.success(i18n.t("kanban.brandModal.updated"));
      } else {
        const res = await api.post(`/brands`, { name });
        data = res.data;
        toast.success(i18n.t("kanban.brandModal.created"));
      }
      onSave(data);
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <div className={classes.dialogHeader}>
        <h2>
          <BusinessIcon style={{ marginRight: 10, fontSize: 22, color: "#3498db" }} />
          {brandId ? i18n.t("kanban.brandModal.editTitle") : i18n.t("kanban.brandModal.addTitle")}
        </h2>
        <Box display="flex" alignItems="center">
          <IconButton
            size="small"
            onClick={() => setShowHelp((prev) => !prev)}
            title="Ayuda / Información"
            style={{ marginRight: 8, color: showHelp ? "#00b4d8" : "inherit" }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="caption"
            style={{ opacity: 0.6, marginRight: 12, fontWeight: 500 }}
          >
            BotMix | CRM
          </Typography>
          <IconButton size="small" onClick={handleClose} aria-label="Cerrar">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </div>

      <Collapse in={showHelp}>
        <div className={classes.helpBox}>
          <Typography variant="body2" style={{ lineHeight: 1.5, opacity: 0.9 }}>
            ℹ️ {i18n.t("kanban.brandModal.summary")}
          </Typography>
        </div>
      </Collapse>

      <DialogContent className={classes.dialogContent}>
        <div className={classes.root}>
          <TextField
            label={i18n.t("kanban.brandModal.brandName")}
            variant="outlined"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </div>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleClose} className={classes.dangerButton}>
          {i18n.t("kanban.brandModal.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          className={classes.applyButton}
          disabled={!name.trim()}
        >
          {i18n.t("kanban.brandModal.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BrandModal;
